import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Hand, ClipboardCheck, Plus, X, AlertTriangle, CheckCircle2, Trash2 } from 'lucide-react';
import { theme, inputStyle, labelStyle } from '../theme';
import { SectionCard, StatCard, StatusPill, TabBar } from '../components/swipe';

// Patient Safety — combined surface for the two big WHO-driven NABH
// modules: Hand Hygiene (5 moments observational audit) and Surgical
// Safety Checklist (sign-in / time-out / sign-out per procedure).
export default function PatientSafetyPage({ API_BASE, currentUser, patientsList }) {
  const [tab, setTab] = useState('dashboard');
  const [dash, setDash] = useState(null);
  const [hhList, setHhList] = useState([]);
  const [sscList, setSscList] = useState([]);
  const [trend, setTrend] = useState(null);
  const [editing, setEditing] = useState(null);   // {kind: 'hh'|'ssc', doc?}

  const load = useCallback(async () => {
    try {
      const [d, h, s, t] = await Promise.all([
        axios.get(`${API_BASE}/safety/dashboard`),
        axios.get(`${API_BASE}/safety/hand-hygiene`),
        axios.get(`${API_BASE}/safety/ssc`),
        axios.get(`${API_BASE}/safety/hand-hygiene/trend?months=6`),
      ]);
      setDash(d.data); setHhList(h.data); setSscList(s.data); setTrend(t.data);
    } catch {}
  }, [API_BASE]);
  useEffect(() => { load(); }, [load]);

  const saveHH = async (payload) => {
    try {
      if (payload._id) await axios.put(`${API_BASE}/safety/hand-hygiene/${payload._id}`, payload);
      else             await axios.post(`${API_BASE}/safety/hand-hygiene`, payload);
      setEditing(null); load();
    } catch (e) { alert(e.response?.data?.error || e.message); }
  };

  const saveSSC = async (payload) => {
    try {
      if (payload._id) await axios.put(`${API_BASE}/safety/ssc/${payload._id}`, payload);
      else             await axios.post(`${API_BASE}/safety/ssc`, payload);
      setEditing(null); load();
    } catch (e) { alert(e.response?.data?.error || e.message); }
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
        <div>
          <h2 style={{ margin: 0, color: theme.navy, fontSize: '1.4rem', fontWeight: 700 }}>Patient Safety</h2>
          <p style={{ margin: '2px 0 0', color: theme.textMuted, fontSize: '0.86rem' }}>
            Hand Hygiene (WHO 5 Moments) + Surgical Safety Checklist — NABH-mandated
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => setEditing({ kind: 'hh', doc: { date: new Date().toISOString().slice(0,10), auditor: currentUser?.username || '', observations: [] } })} style={ghostBtn}><Hand size={14}/> + Hand Hygiene Audit</button>
          <button onClick={() => setEditing({ kind: 'ssc', doc: null })} style={primaryBtn}><ClipboardCheck size={14}/> + Surgical Checklist</button>
        </div>
      </div>

      {dash && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 18 }}>
          <StatCard label="Hand Hygiene Compliance (30d)" value={`${dash.handHygiene.compliancePercent}%`}
                    tone={dash.handHygiene.targetMet ? 'success' : 'warning'} sub={`Target ≥80% · ${dash.handHygiene.audits30d} audits`} icon={<Hand size={22}/>}/>
          <StatCard label="Surgical Checklists (30d)" value={dash.surgicalSafety.checklists30d}
                    tone="navy" sub={`${dash.surgicalSafety.complete} complete, ${dash.surgicalSafety.incomplete} incomplete`} icon={<ClipboardCheck size={22}/>}/>
          <StatCard label="Avg SSC Completion" value={`${dash.surgicalSafety.avgCompletion}%`}
                    tone={dash.surgicalSafety.avgCompletion >= 90 ? 'success' : 'warning'} sub="Across all procedures" icon={<CheckCircle2 size={22}/>}/>
          <StatCard label="Adverse Events (30d)" value={dash.surgicalSafety.adverseEvents}
                    tone={dash.surgicalSafety.adverseEvents === 0 ? 'success' : 'danger'} sub="Linked to incidents" icon={<AlertTriangle size={22}/>}/>
        </div>
      )}

      <TabBar
        tabs={[
          { key: 'dashboard',     label: 'Dashboard' },
          { key: 'hand-hygiene',  label: 'Hand Hygiene', count: hhList.length },
          { key: 'ssc',           label: 'Surgical Safety', count: sscList.length },
        ]}
        active={tab} onChange={setTab} variant="underline"
      />

      {tab === 'dashboard' && trend && (
        <SectionCard variant="white" title="Hand Hygiene Compliance — 6 Month Trend">
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr><th style={th}>Month</th><th style={th}>Audits</th><th style={th}>Moments observed</th><th style={th}>Compliance %</th></tr></thead>
            <tbody>
              {trend.months.length === 0 ? (
                <tr><td colSpan="4" style={{ ...td, textAlign: 'center', color: theme.textMuted }}>No audits yet</td></tr>
              ) : trend.months.map(m => (
                <tr key={m.month}>
                  <td style={td}>{m.month}</td>
                  <td style={td}>{m.audits}</td>
                  <td style={td}>{m.compliant} / {m.total}</td>
                  <td style={td}>
                    <span style={{ color: m.compliancePercent >= 80 ? theme.success : m.compliancePercent >= 60 ? theme.warning : theme.danger, fontWeight: 700 }}>
                      {m.compliancePercent}%
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ marginTop: 12, padding: 10, backgroundColor: theme.sectionMint, borderRadius: 8, fontSize: '0.86rem', color: theme.navy }}>
            Overall: <strong>{trend.overallCompliance}%</strong> · {trend.totalAudits} total audits
          </div>
        </SectionCard>
      )}

      {tab === 'hand-hygiene' && (
        <SectionCard variant="white" padding="0">
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr><th style={th}>Date</th><th style={th}>Area</th><th style={th}>Auditor</th><th style={th}>Observations</th><th style={th}>Compliance</th><th style={th}></th></tr></thead>
            <tbody>
              {hhList.length === 0 ? (
                <tr><td colSpan="6" style={{ ...td, textAlign: 'center', color: theme.textMuted, padding: 30 }}>No audits yet</td></tr>
              ) : hhList.map(a => (
                <tr key={a._id}>
                  <td style={td}>{new Date(a.date).toLocaleDateString('en-IN')}</td>
                  <td style={td}>{a.area || '—'}</td>
                  <td style={td}>{a.auditor}</td>
                  <td style={td}>{a.compliantMoments}/{a.totalMoments}</td>
                  <td style={td}>
                    <span style={{ color: a.compliancePercent >= 80 ? theme.success : theme.danger, fontWeight: 700 }}>
                      {a.compliancePercent}%
                    </span>
                  </td>
                  <td style={td}>
                    <button onClick={() => setEditing({ kind: 'hh', doc: a })} style={iconBtn}>Edit</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </SectionCard>
      )}

      {tab === 'ssc' && (
        <SectionCard variant="white" padding="0">
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr><th style={th}>Date</th><th style={th}>Patient</th><th style={th}>Procedure</th><th style={th}>Dentist</th><th style={th}>Completion</th><th style={th}>Status</th><th style={th}></th></tr></thead>
            <tbody>
              {sscList.length === 0 ? (
                <tr><td colSpan="7" style={{ ...td, textAlign: 'center', color: theme.textMuted, padding: 30 }}>No checklists yet</td></tr>
              ) : sscList.map(s => (
                <tr key={s._id} style={s.adverseEvent ? { backgroundColor: '#fff7ed' } : {}}>
                  <td style={td}>{new Date(s.date).toLocaleDateString('en-IN')}</td>
                  <td style={td}>{s.patientName}</td>
                  <td style={td}>{s.procedureName} {s.toothSite && <span style={{ color: theme.textMuted }}>· {s.toothSite}</span>}</td>
                  <td style={td}>{s.operatingDentist}</td>
                  <td style={td}>
                    <span style={{ color: s.completionPercent >= 90 ? theme.success : s.completionPercent >= 60 ? theme.warning : theme.danger, fontWeight: 700 }}>
                      {s.completionPercent}%
                    </span>
                  </td>
                  <td style={td}>
                    <StatusPill preset={s.status === 'complete' ? 'success' : s.status === 'in_progress' ? 'warning' : 'neutral'} label={s.status?.replace('_', ' ')} size="sm"/>
                    {s.adverseEvent && <StatusPill preset="danger" label="Adverse" size="sm" style={{ marginLeft: 6 }}/>}
                  </td>
                  <td style={td}>
                    <button onClick={() => setEditing({ kind: 'ssc', doc: s })} style={iconBtn}>Open</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </SectionCard>
      )}

      {editing?.kind === 'hh'  && <HHAuditForm doc={editing.doc} onSave={saveHH}  onClose={() => setEditing(null)}/>}
      {editing?.kind === 'ssc' && <SSCForm     doc={editing.doc} onSave={saveSSC} onClose={() => setEditing(null)} patientsList={patientsList} API_BASE={API_BASE}/>}
    </div>
  );
}

const MOMENTS = [
  '1. Before patient contact',
  '2. Before aseptic procedure',
  '3. After body fluid exposure',
  '4. After patient contact',
  '5. After contact with patient surroundings',
];

function HHAuditForm({ doc, onSave, onClose }) {
  const [d, setD] = useState({ date: '', auditor: '', area: '', durationMin: 30, observations: [], notes: '', ...doc });
  const upd = (patch) => setD(prev => ({ ...prev, ...patch }));
  const addObs = () => upd({ observations: [...d.observations, { staffName: '', role: '', moment: 1, performed: false, method: 'none' }] });
  const updObs = (i, patch) => upd({ observations: d.observations.map((o, idx) => idx === i ? { ...o, ...patch } : o) });
  const rmObs  = (i) => upd({ observations: d.observations.filter((_, idx) => idx !== i) });

  const compliantCount = d.observations.filter(o => o.performed).length;
  const complianceNow = d.observations.length === 0 ? 0 : Math.round((compliantCount / d.observations.length) * 100);

  return (
    <div onClick={onClose} style={overlay}>
      <div onClick={e => e.stopPropagation()} style={sheet}>
        <div style={sheetHead}>
          <h3 style={{ margin: 0 }}>Hand Hygiene Audit · {complianceNow}% live</h3>
          <button onClick={onClose} style={iconBtn}><X size={18}/></button>
        </div>
        <div style={{ padding: 20, overflowY: 'auto', maxHeight: 'calc(85vh - 60px)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <div><label style={labelStyle}>Date</label><input type="date" value={d.date?.slice(0,10) || ''} onChange={e => upd({ date: e.target.value })} style={inputStyle}/></div>
            <div><label style={labelStyle}>Area / Chair</label><input value={d.area} onChange={e => upd({ area: e.target.value })} placeholder="e.g. Chair 1" style={inputStyle}/></div>
            <div><label style={labelStyle}>Auditor *</label><input value={d.auditor} onChange={e => upd({ auditor: e.target.value })} style={inputStyle}/></div>
          </div>

          <h4 style={{ marginTop: 20, marginBottom: 10, color: theme.navy }}>Observations</h4>
          {(d.observations || []).map((o, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '2fr 1.5fr 2fr 1.2fr 0.8fr 30px', gap: 6, marginBottom: 6 }}>
              <input value={o.staffName} onChange={e => updObs(i, { staffName: e.target.value })} placeholder="Staff name" style={inputStyle}/>
              <input value={o.role}      onChange={e => updObs(i, { role: e.target.value })}      placeholder="Role" style={inputStyle}/>
              <select value={o.moment} onChange={e => updObs(i, { moment: Number(e.target.value) })} style={inputStyle}>
                {MOMENTS.map((m, idx) => <option key={idx} value={idx + 1}>{m}</option>)}
              </select>
              <select value={o.method} onChange={e => updObs(i, { method: e.target.value, performed: e.target.value !== 'none' })} style={inputStyle}>
                <option value="none">Missed</option>
                <option value="handrub">Hand-rub</option>
                <option value="handwash">Wash</option>
              </select>
              <span style={{ alignSelf: 'center', textAlign: 'center', fontWeight: 700, color: o.performed ? theme.success : theme.danger }}>{o.performed ? '✓' : '✗'}</span>
              <button onClick={() => rmObs(i)} style={iconBtn}><Trash2 size={12}/></button>
            </div>
          ))}
          <button onClick={addObs} style={ghostBtn}><Plus size={12}/> Add observation</button>

          <label style={{ ...labelStyle, marginTop: 14 }}>Notes</label>
          <textarea rows={2} value={d.notes} onChange={e => upd({ notes: e.target.value })} style={{ ...inputStyle, resize: 'vertical' }}/>

          <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
            <button onClick={onClose} style={ghostBtn}>Cancel</button>
            <button onClick={() => onSave(d)} style={primaryBtn}>Save Audit</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function SSCForm({ doc, onSave, onClose, patientsList = [], API_BASE }) {
  const [d, setD] = useState(null);

  useEffect(() => {
    if (doc) { setD({ ...doc }); return; }
    // Fresh checklist — pull default items from server.
    axios.get(`${API_BASE}/safety/ssc/defaults`).then(r => {
      setD({
        date: new Date().toISOString().slice(0,10),
        patientId: '', patientName: '', procedureName: '', toothSite: '',
        operatingDentist: '', assistant: '',
        signIn:  r.data.signIn.map(l => ({ label: l, completed: false })),
        timeOut: r.data.timeOut.map(l => ({ label: l, completed: false })),
        signOut: r.data.signOut.map(l => ({ label: l, completed: false })),
        adverseEvent: false, adverseDetails: '', status: 'draft',
      });
    });
  }, [doc, API_BASE]);

  if (!d) return null;
  const upd = (patch) => setD(prev => ({ ...prev, ...patch }));
  const togglePhase = (phase, idx) => upd({ [phase]: d[phase].map((it, i) => i === idx ? { ...it, completed: !it.completed } : it) });

  const Phase = ({ title, items, phase, color }) => (
    <SectionCard variant="white" title={title}>
      {items.map((it, idx) => (
        <label key={idx} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: 6, borderRadius: 6, cursor: 'pointer' }}
          onMouseEnter={e => e.currentTarget.style.backgroundColor = theme.bg}
          onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
          <input type="checkbox" checked={it.completed} onChange={() => togglePhase(phase, idx)} style={{ marginTop: 3, accentColor: color }}/>
          <span style={{ color: it.completed ? theme.success : theme.navy, textDecoration: it.completed ? 'line-through' : 'none', fontSize: '0.9rem' }}>{it.label}</span>
        </label>
      ))}
    </SectionCard>
  );

  return (
    <div onClick={onClose} style={overlay}>
      <div onClick={e => e.stopPropagation()} style={{ ...sheet, maxWidth: 880 }}>
        <div style={sheetHead}>
          <h3 style={{ margin: 0 }}>{d._id ? 'Surgical Safety Checklist' : 'New Surgical Safety Checklist'} · {d.completionPercent || 0}%</h3>
          <button onClick={onClose} style={iconBtn}><X size={18}/></button>
        </div>
        <div style={{ padding: 20, overflowY: 'auto', maxHeight: 'calc(90vh - 60px)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr 1fr', gap: 12 }}>
            <div>
              <label style={labelStyle}>Patient *</label>
              <select value={d.patientId} onChange={e => {
                const p = patientsList.find(x => x._id === e.target.value);
                upd({ patientId: e.target.value, patientName: p?.name || '' });
              }} style={inputStyle}>
                <option value="">Select patient</option>
                {patientsList.map(p => <option key={p._id} value={p._id}>{p.name} ({p.uhid || p.phone})</option>)}
              </select>
            </div>
            <div><label style={labelStyle}>Date</label><input type="date" value={d.date?.slice(0,10) || ''} onChange={e => upd({ date: e.target.value })} style={inputStyle}/></div>
            <div><label style={labelStyle}>Tooth / Site</label><input value={d.toothSite} onChange={e => upd({ toothSite: e.target.value })} placeholder="e.g. 16, upper right" style={inputStyle}/></div>
            <div style={{ gridColumn: 'span 2' }}><label style={labelStyle}>Procedure *</label><input value={d.procedureName} onChange={e => upd({ procedureName: e.target.value })} placeholder="e.g. RCT, Extraction" style={inputStyle}/></div>
            <div><label style={labelStyle}>Operating Dentist *</label><input value={d.operatingDentist} onChange={e => upd({ operatingDentist: e.target.value })} style={inputStyle}/></div>
          </div>

          <div style={{ marginTop: 18 }}>
            <Phase title="🔹 SIGN-IN (before LA)"   items={d.signIn}  phase="signIn"  color={theme.info}/>
            <Phase title="🟡 TIME-OUT (before procedure)" items={d.timeOut} phase="timeOut" color={theme.warning}/>
            <Phase title="🟢 SIGN-OUT (before patient leaves)" items={d.signOut} phase="signOut" color={theme.success}/>
          </div>

          <SectionCard variant="cream" bordered title="Adverse Event">
            <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="checkbox" checked={d.adverseEvent} onChange={e => upd({ adverseEvent: e.target.checked })}/>
              <span style={{ color: theme.navy, fontWeight: 600 }}>Adverse event occurred during procedure</span>
            </label>
            {d.adverseEvent && (
              <textarea value={d.adverseDetails} onChange={e => upd({ adverseDetails: e.target.value })} rows={2} placeholder="Brief description — full incident report should be filed separately" style={{ ...inputStyle, resize: 'vertical', marginTop: 8 }}/>
            )}
          </SectionCard>

          <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
            <button onClick={onClose} style={ghostBtn}>Cancel</button>
            <button onClick={() => onSave(d)} style={primaryBtn}>Save Checklist</button>
          </div>
        </div>
      </div>
    </div>
  );
}

const primaryBtn = { padding: '9px 16px', backgroundColor: theme.primary, color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.85rem' };
const ghostBtn   = { padding: '7px 14px', backgroundColor: theme.surface, color: theme.navy, border: `1px solid ${theme.border}`, borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: '0.82rem', display: 'inline-flex', alignItems: 'center', gap: 6 };
const iconBtn    = { padding: '5px 9px', backgroundColor: 'transparent', color: theme.navy, border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: '0.78rem' };
const td = { padding: '10px 14px', borderBottom: `1px solid ${theme.borderLight}`, fontSize: '0.86rem', color: theme.navy };
const th = { padding: '11px 14px', borderBottom: `1px solid ${theme.border}`, color: theme.textMuted, textAlign: 'left', fontSize: '0.74rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', backgroundColor: '#fafbfc' };
const overlay = { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 };
const sheet = { backgroundColor: '#fff', borderRadius: 14, width: '95%', maxWidth: 760, maxHeight: '90vh', display: 'flex', flexDirection: 'column' };
const sheetHead = { padding: '16px 20px', borderBottom: `1px solid ${theme.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' };
